import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Edit, Search } from '@mui/icons-material';
import {
  Link,
  Typography,
  Tooltip,
  IconButton,
  Button,
  Box,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { NumberField } from '@base-ui-components/react/number-field';
import { useMaterialReactTable, MRT_Table as MRT } from 'material-react-table';
import {
  upsertDeploymentConfigAction,
  startDeploymentDiscoveryAction,
} from '../../redux/actions/deployments';
import BuildingSelector from '../buildingselector/BuildingSelector';
import { AUTH_LEVELS } from '../../redux/constants/roles';
import { defaultTableOptions } from '../util/DataTable';

const END_STATES = ['FAILED', 'SUCCESS', 'No History'];

const DeploymentConfiguration = ({
  deployment,
  config,
  discoveries,
  collectorIps,
  numCollectors,
  deploymentActive,
  canDiscover,
  startDeploymentDiscovery,
  updateDeploymentConfig,
}) => {
  const [discoveryPort, setDiscoveryPort] = React.useState(null);
  const [building, setBuilding] = useState(deployment.buildings[0]);

  const renderStatus = useCallback(
    (renderedCellValue) => {
      if (renderedCellValue === 'NEW')
        return (
          <Tooltip title="Discoveries are NEW until the collector starts performing the discovery">
            {renderedCellValue}
          </Tooltip>
        );
      if (renderedCellValue === 'SUCCESS')
        return (
          <Link href={`/staging?view=Devices&bid=${building}`} color="#000000">
            {renderedCellValue}
          </Link>
        );
      if (renderedCellValue === 'FAILED')
        return (
          <Typography variant="button" color="error">
            {renderedCellValue}
          </Typography>
        );
      return (
        <Typography variant="button" color="grey">
          {renderedCellValue}
        </Typography>
      );
    },
    [building]
  );

  const data = config?.bacnet?.map((bc) => ({
    status: 'No History',
    buildings: deployment?.buildings?.length,
    ...bc,
    ...discoveries.filter((d) => d.port === bc.port)[0],
  }));

  const columns = useMemo(
    () => [
      {
        accessorKey: 'network',
        header: 'Network',
        editVariant: 'select',
        editSelectOptions: collectorIps,
        muiEditTextFieldProps: {
          required: true,
          select: true,
        },
      },
      {
        accessorKey: 'port',
        header: 'Port',
        enableEditing: ({ index }) => index === -1,
        muiEditTextFieldProps: {
          required: true,
          type: 'number',
          inputProps: {
            min: 1,
            max: 65535,
          },
        },
      },
      {
        accessorKey: 'poll_freq_secs',
        header: 'Poll Frequency (Seconds)',
        muiEditTextFieldProps: {
          type: 'number',
          required: true,
          inputProps: {
            min: 60,
          },
        },
      },
      {
        accessorKey: 'status',
        header: 'Latest Discovery Status',
        enableEditing: false,

        Cell: ({ renderedCellValue }) => renderStatus(renderedCellValue),
      },
      {
        accessorKey: 'creator',
        header: 'Latest Discovery Creator',
        enableEditing: false,
      },
    ],
    [renderStatus, collectorIps]
  );

  const bacnetTable = useMaterialReactTable({
    ...defaultTableOptions(false),
    columns,
    data,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    editDisplayMode: 'row',
    enableBottomToolbar: true,
    enableEditing: canDiscover,
    enableRowActions: canDiscover,
    getRowId: (row) => row.port,
    positionActionsColumn: 'last',
    muiEditTextFieldProps: {
      variant: 'outlined',
    },
    onEditingRowSave: ({ table }) => {
      // values should contain the right value, but for some reason it doesn't, so instead we'll pull the edit rows out
      // eslint-disable-next-line no-underscore-dangle
      updateDeploymentConfig(deployment, { ...table.getState().editingRow._valuesCache }).finally(
        () => table.setEditingRow(null)
      );
    },
    renderRowActions: ({ table, row }) => {
      let disabled = false;
      let message = `Start discovery on bacnet port ${row.original.port}`;
      if (!deploymentActive) {
        disabled = true;
        message = `Unable to run discovery for deployment: deployment not active`;
      } else if (row.original.buildings === 0) {
        disabled = true;
        message = `Unable to run discovery for deployment: deployment has no buildings`;
      } else if (numCollectors === 0) {
        disabled = true;
        message = `Unable to run discovery for deployment: deployment has no active collectors`;
      } else if (numCollectors > 1) {
        disabled = true;
        message = `Unable to run discovery for deployment: deployment has multiple active collectors`;
      } else if (!END_STATES.includes(row.original.status)) {
        disabled = true;
        message = `Unable to run discovery for deployment: discovery already in progress`;
      }
      return (
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          <Tooltip title="Edit">
            <IconButton onClick={() => table.setEditingRow(row)}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip tag="discover" title={message} arrow>
            {
              // disabled tooltip hack https://github.com/mui/material-ui/issues/33182#issuecomment-2823321971
            }
            <span aria-label={undefined}>
              <IconButton onClick={() => setDiscoveryPort(row.original.port)} disabled={disabled}>
                <Search />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    },
  });

  function handleSubmit(event) {
    event.preventDefault();
    startDeploymentDiscovery(
      deployment,
      building,
      discoveryPort,
      parseInt(event.currentTarget.elements.min.value, 10),
      parseInt(event.currentTarget.elements.max.value, 10)
    );
    setDiscoveryPort(null);
  }

  return (
    <>
      <Dialog open={discoveryPort != null} onClose={() => setDiscoveryPort(null)}>
        <DialogTitle>
          Start new discovery for {deployment.name} for bacnet port {discoveryPort}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            When discovery is running there will be a period of downtime for real time data
            collection. This will create a gap in the historical record for the duration of the
            discovery process., <br />
            <br />
            Please update device id range if you wish to target discovery (the default range is
            [1-4194302]):
          </DialogContentText>
          <form onSubmit={handleSubmit} id="discovery-form">
            <DialogContentText>Minimum Device Id:</DialogContentText>
            <NumberField.Root id="min" defaultValue={1} min={1} format={{ useGrouping: false }}>
              <NumberField.Input />
            </NumberField.Root>
            <DialogContentText>Maximum Device Id:</DialogContentText>
            <NumberField.Root
              id="max"
              defaultValue={4194302}
              max={4194302}
              format={{ useGrouping: false }}
            >
              <NumberField.Input />
            </NumberField.Root>
            {deployment?.buildings?.length !== 1 ? (
              <>
                <DialogContentText>Select a building for new devices</DialogContentText>
                <BuildingSelector
                  id="bid"
                  selectedBuildingId={building}
                  filter={(b) => deployment.buildings.indexOf(b.id) !== -1}
                  disabled={deployment?.buildings?.length === 1}
                  handleSelection={({ target }) => setBuilding(target.value)}
                />
              </>
            ) : null}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscoveryPort(null)}>Cancel</Button>
          <Button type="submit" form="discovery-form">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <MRT table={bacnetTable} />
    </>
  );
};

function mapStateToProps(state, { deployment }) {
  const {
    entities: {
      deployments: { configs, discovery },
    },
    user: { userAuthLevel },
  } = state;
  const activeCollectors = configs[deployment.site]?.collectors?.filter((c) => c.active) ?? [];
  return {
    config: configs[deployment.site]?.configs ?? {},
    collectorIps: activeCollectors.flatMap((c) => c.device_ips),
    // gate behind superadmin utnil ready to release
    canDiscover: userAuthLevel <= AUTH_LEVELS.ORG_ADMIN,
    discoveries: discovery[deployment.site] ?? [],
    numCollectors: activeCollectors?.length ?? 0,
    deploymentActive: deployment.active,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    startDeploymentDiscovery: (deployment, buildingId, port, min, max) =>
      dispatch(startDeploymentDiscoveryAction(deployment.site, buildingId, port, min, max)),
    updateDeploymentConfig: (deployment, { port, network, poll_freq_secs }) =>
      dispatch(upsertDeploymentConfigAction(deployment.site, { port, network, poll_freq_secs })),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DeploymentConfiguration);
